import { arrayBufferToHex, hmac, sha256 } from '@gem/utils';

import { X_AMZ_ALGORITHM } from '../constants';
import { IStorageSessionCredentials, TStorageSessionRequestMethod } from '../types';
import { getAmzDateStamp } from './getAmzDateStamp';
import { getCanonicalRequestHash } from './getCanonicalRequestHash';
import { getCredentialsScope } from './getCredentialsScope';
import { getRemoteStorageSigningKeyBuffer } from './getRemoteStorageSigningKeyBuffer';

interface ISignedFileRequestData {
  url: string;
  headers: Record<string, string>;
}

export async function getSignedFileRequestData(
  resourceUrl: string,
  credentials: IStorageSessionCredentials,
  method: TStorageSessionRequestMethod = 'GET'
): Promise<ISignedFileRequestData | null> {
  if (!resourceUrl.includes(credentials.endpoint)) {
    return null;
  }

  const { amzDate, dateStamp } = getAmzDateStamp();
  const endpointUrlObject = new URL(credentials.endpoint);
  const canonicalUrlObject = new URL(resourceUrl);
  const host = endpointUrlObject.host;

  const canonicalUri = canonicalUrlObject.pathname + canonicalUrlObject.search;

  // Хэш пустого payload для GET
  const payloadHash = arrayBufferToHex(await sha256(''));

  const headers: Record<string, string> = {
    host,
    'x-amz-content-sha256': payloadHash,
    'x-amz-date': amzDate,
    'x-amz-security-token': credentials.sessionToken,
  };

  const signedHeaders = Object.keys(headers).sort().join(';');
  const canonicalHeaders = Object.keys(headers)
    .sort()
    .map((k) => `${k}:${headers[k]}\n`)
    .join('');

  const canonicalRequestHash = await getCanonicalRequestHash({
    method,
    canonicalUri,
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  });

  const scope = getCredentialsScope(credentials, dateStamp);
  const stringToSign = [X_AMZ_ALGORITHM, amzDate, scope, canonicalRequestHash].join('\n');

  const keyBuf = await getRemoteStorageSigningKeyBuffer(credentials, dateStamp);
  const signature = arrayBufferToHex(await hmac(keyBuf, stringToSign));

  const authorization =
    `${X_AMZ_ALGORITHM} Credential=${credentials.accessKeyId}/${scope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`;

  const requestUrl = `${credentials.endpoint}${canonicalUri}`;
  return {
    url: requestUrl,
    headers: {
      ...headers,
      Authorization: authorization,
    },
  };
}
